import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiClient, { api } from '../api/client';
import { User, LoginResponse } from '@snippix/shared';

/* -------------------------------------------------------------------------- *
 * Vite environment typings (fixes `import.meta.env` errors in strict TS)     *
 * -------------------------------------------------------------------------- */

// Define the shape of our authentication context
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
  refreshToken: async () => false,
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Initialize auth state from localStorage on component mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('snippix_access_token');
        const storedUser = localStorage.getItem('snippix_user');

        if (storedToken && storedUser) {
          // Set the token in state
          setAccessToken(storedToken);

          // Parse and set the user
          setUser(JSON.parse(storedUser));

          // Verify token is still valid by fetching current user
          try {
            await api.auth.me();
          } catch (error) {
            // Token might be expired, try to refresh
            const refreshed = await refreshToken();
            if (!refreshed) {
              // If refresh failed, clear auth state
              await logout();
            }
          }
        } else {
          // No stored auth, clear any lingering state
          setAccessToken(null);
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear auth state on error
        setAccessToken(null);
        setUser(null);
        localStorage.removeItem('snippix_access_token');
        localStorage.removeItem('snippix_user');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /* ------------------------------------------------------------------------ *
   * Keep axios default Authorization header in sync with accessToken state   *
   * ------------------------------------------------------------------------ */
  useEffect(() => {
    if (accessToken) {
      apiClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      delete apiClient.defaults.headers.common.Authorization;
    }
  }, [accessToken]);

  // Login function
  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);

      // ------------------------------------------------------------------
      // Verbose DEBUG logging – helps diagnose 404 / CORS / wrong-URL bugs
      // ------------------------------------------------------------------
      /* eslint-disable no-console */
      if (import.meta.env.DEV) {
        console.log('[AuthContext] 🔐 login(): making POST request', {
          url: `${apiClient.defaults.baseURL}/auth/login`,
          email,
          // NOTE: never log the raw password – only its length
          passwordLength: password.length,
        });
      }
      /* eslint-enable no-console */

      // Regular auth endpoint on main backend
      const response = await api.auth.login<LoginResponse>({ email, password });

      const { accessToken: token, user: userData } = response;

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[AuthContext] 🔐 login(): server response OK', {
          userId: userData.id,
          role: userData.role,
          tokenPreview: token.substring(0, 12) + '…',
        });
      }

      // Store token and user data
      localStorage.setItem('snippix_access_token', token);
      localStorage.setItem('snippix_user', JSON.stringify(userData));

      // Update state
      setAccessToken(token);
      setUser(userData);

      return;
    } catch (error) {
      // Extra debug output
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error('[AuthContext] 🔐 login(): error', error);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true);

      // Call logout endpoint if authenticated
      if (isAuthenticated) {
        await api.auth.logout();
      }

      // Clear stored auth data
      localStorage.removeItem('snippix_access_token');
      localStorage.removeItem('snippix_user');

      // Reset state
      setAccessToken(null);
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Token refresh function
  const refreshToken = async (): Promise<boolean> => {
    try {
      const { accessToken: newToken } = await api.auth.refresh<{ accessToken: string }>();

      // Update token in storage and state
      localStorage.setItem('snippix_access_token', newToken);
      setAccessToken(newToken);

      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  };

  // Set up axios interceptor to handle token expiration
  useEffect(() => {
    // Note: We don't need to set up an interceptor here anymore
    // since apiClient already has token refresh logic built-in

    // However, we still need to handle the case where the token refresh
    // fails and we need to log the user out
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'snippix_access_token' && !e.newValue) {
        // Token was removed by another tab/window
        setUser(null);
        setAccessToken(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Provide the auth context to children
  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
